import { Component, OnInit, OnDestroy } from '@angular/core';
import { FirestoreDataService } from '../core/firestore-data.service';
import { Product } from '../core/models';
import { Observable, BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { FormControl } from '@angular/forms';

@Component({
  selector: 'app-inventory',
  templateUrl: './inventory.component.html',
  styleUrls: ['./inventory.component.scss'],
  standalone: false
})
export class InventoryComponent implements OnInit, OnDestroy {
  isLoading = true;
  private dataSubscription?: Subscription;
  
  // Lista maestra de familias autorizadas según las categorías de ServiTrack
  readonly families = [
    'PRESION DE AIRE Y NEUMATICOS',
    'HERRAMIENTAS',
    'PIEZAS Y COMPONENTES',
    'MANTENIMIENTO',
    'REPUESTO',
    'TRANSMISION',
    'FRENOS',
    'SUSPENCIONES',
    'INSUMOS'
  ];

  // Controles para los filtros de la UI
  categoryFilter = new FormControl('ALL');
  searchTerm = new FormControl('');

  // Sujeto para manejar el estado local de los productos procesados
  private allActiveProductsSubject = new BehaviorSubject<Product[]>([]);
  filteredProducts$!: Observable<Product[]>;

  constructor(private firestoreDataService: FirestoreDataService) {}

  ngOnInit(): void {
    console.log('[Inventory] Iniciando componente...');
    
    // 1. Forzamos la carga de productos desde el servicio
    console.log('[Inventory] Llamando a loadProducts() en el servicio...');
    this.firestoreDataService.loadProducts();
    
    // 2. Suscripción maestra al flujo de datos de Firestore
    this.dataSubscription = this.firestoreDataService.products$.subscribe({
      next: (products) => {
        console.log('[Inventory] Data recibida del servicio. Cantidad total:', products?.length || 0);
        
        if (products && products.length > 0) {
          // Log de una muestra para verificar nombres de campos (Case sensitivity)
          console.log('[Inventory] Muestra del primer producto crudo:', products[0]);

          const processed = products
            .map(p => {
              const rawFamily = p.Familia || '';
              const rawHiopos = p.HIOPOS || '';
              return {
                ...p,
                // Normalización crítica: eliminamos espacios y forzamos mayúsculas
                Familia: rawFamily.trim().toUpperCase(),
                HIOPOS: rawHiopos.trim().toUpperCase()
              };
            })
            .filter(p => {
              // Regla de negocio: Solo productos activos y que pertenezcan a nuestras familias
              const isActivo = p.HIOPOS === 'ACTIVO' || p.HIOPOS === '';
              const hasValidFamily = this.families.includes(p.Familia);
              
              if (!hasValidFamily && p.Familia !== '') {
                console.warn(`[Inventory] Producto "${p.nombre}" descartado por familia no reconocida: "${p.Familia}"`);
              }

              return isActivo && hasValidFamily;
            });

          console.log('[Inventory] Productos después de filtrar por Familia y HIOPOS:', processed.length);
          this.allActiveProductsSubject.next(processed);
          this.isLoading = false;
        } else if (products && products.length === 0) {
          console.warn('[Inventory] El servicio devolvió un array vacío [].');
          this.isLoading = false;
        }
      },
      error: (err) => {
        console.error("[Inventory] Error crítico en la suscripción:", err);
        this.isLoading = false;
      }
    });

    // 3. Lógica reactiva de filtros combinados (Categoría + Texto)
    this.filteredProducts$ = combineLatest([
      this.allActiveProductsSubject.asObservable(),
      this.categoryFilter.valueChanges.pipe(startWith('ALL')),
      this.searchTerm.valueChanges.pipe(startWith(''))
    ]).pipe(
      map(([products, cat, search]) => {
        console.log(`[Inventory] Aplicando filtros UI -> Cat: ${cat}, Search: ${search}`);
        return products.filter(p => {
          // Filtro por Categoría
          const matchesCat = cat === 'ALL' || p.Familia === cat;
          
          // Filtro por término de búsqueda (Nombre, Referencia o Marca)
          const term = (search || '').toLowerCase().trim();
          const matchesSearch = !term || 
                               p.nombre.toLowerCase().includes(term) || 
                               p.id.toLowerCase().includes(term) ||
                               (p.marca && p.marca.toLowerCase().includes(term));
                               
          return matchesCat && matchesSearch;
        });
      })
    );

    // Timeout de seguridad para evitar loops infinitos en caso de error de red
    setTimeout(() => { 
      if (this.isLoading) {
        console.warn('[Inventory] El tiempo de espera se agotó (6s). Forzando cierre de loading.');
        this.isLoading = false; 
      }
    }, 6000);
  }

  /**
   * Limpieza de suscripciones para prevenir fugas de memoria
   */
  ngOnDestroy(): void {
    console.log('[Inventory] Destruyendo componente y liberando suscripción.');
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }
  }
}